import { type FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { CHART_PRIMARY_COLOR, CHART_SECONDARY_COLOR } from "src/components/charts/chartPalette";
import { DefinitionTooltip, type DefinitionTooltipInfo } from "src/components/common";
import { useLanguage } from "src/hooks/useLanguage";
import {
  buildCustomerSegmentationModel,
  type CustomerSegmentationModel,
} from "../../utils/customerSegmentationModel";
import { localizeChartDigits } from "../salesProductShared";
import "./CustomerSegmentationKpi.scss";

const SEGMENT_COLORS = [CHART_PRIMARY_COLOR, CHART_SECONDARY_COLOR] as const;

type PieRow = { label: string; value: number; segmentIndex: number };

const SegmentationTooltip: FC<{
  row: PieRow;
  model: CustomerSegmentationModel;
  isRTL: boolean;
}> = ({ row, model, isRTL }) => {
  const pct =
    row.segmentIndex === 0
      ? model.newSharePctDisplay
      : model.returningSharePctDisplay;

  return (
    <>
      <div className="customer-segmentation-kpi__chart-tooltip-label">{row.label}</div>
      <div className="customer-segmentation-kpi__chart-tooltip-metric">
        <span className="customer-segmentation-kpi__chart-tooltip-count">
          {localizeChartDigits(Number(row.value).toLocaleString(), isRTL)}
        </span>
        <span className="customer-segmentation-kpi__chart-tooltip-pct">
          {localizeChartDigits(`${pct}%`, isRTL)}
        </span>
      </div>
    </>
  );
};

export type CustomerSegmentationKpiProps = {
  newCount: number;
  returningCount: number;
  loading?: boolean;
};

export const CustomerSegmentationKpi: FC<CustomerSegmentationKpiProps> = ({
  newCount,
  returningCount,
  loading = false,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const model = useMemo(
    () => buildCustomerSegmentationModel(newCount, returningCount),
    [newCount, returningCount],
  );
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const newLabel = t("salesProduct.customers.segmentation.newLabel");
  const returningLabel = t("salesProduct.customers.segmentation.returningLabel");
  const segmentTooltips: Record<"new" | "returning", DefinitionTooltipInfo> = {
    new: {
      description: t("salesProduct.customers.segmentation.newTooltip.description"),
      formula: t("salesProduct.customers.segmentation.newTooltip.formula"),
    },
    returning: {
      description: t("salesProduct.customers.segmentation.returningTooltip.description"),
      formula: t("salesProduct.customers.segmentation.returningTooltip.formula"),
    },
  };

  const pieData: PieRow[] = useMemo(
    () => [
      { label: newLabel, value: model.newCount, segmentIndex: 0 },
      { label: returningLabel, value: model.returningCount, segmentIndex: 1 },
    ],
    [model.newCount, model.returningCount, newLabel, returningLabel],
  );

  if (loading) {
    return (
      <div className="customer-segmentation-kpi customer-segmentation-kpi--empty" />
    );
  }

  if (model.mixTotal === 0) {
    return (
      <div className="customer-segmentation-kpi customer-segmentation-kpi--empty">
        <p className="customer-segmentation-kpi__empty-text">
          {t("salesProduct.customers.segmentation.empty")}
        </p>
      </div>
    );
  }

  const chartSummary = t("salesProduct.customers.segmentation.chartSummary", {
    newShare: localizeChartDigits(String(model.newSharePctDisplay), isRTL),
    returningShare: localizeChartDigits(String(model.returningSharePctDisplay), isRTL),
    total: localizeChartDigits(model.mixTotal.toLocaleString(), isRTL),
  });
  const hoveredRow = hoveredIndex === null ? null : pieData[hoveredIndex];

  return (
    <div className="customer-segmentation-kpi">
      <p className="customer-segmentation-kpi__subhead">
        {t("salesProduct.customers.segmentation.subhead")}
      </p>

      <div className="customer-segmentation-kpi__body">
        <div
          className="customer-segmentation-kpi__chart-block"
          role="img"
          aria-label={chartSummary}
        >
          <div className="customer-segmentation-kpi__chart-surface">
            <div className="customer-segmentation-kpi__donut-frame">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="label"
                    cx="50%"
                    cy="50%"
                    innerRadius="56%"
                    outerRadius="88%"
                    paddingAngle={0}
                    cornerRadius={0}
                    stroke="none"
                    strokeWidth={0}
                    isAnimationActive={false}
                    onMouseEnter={(_, index) => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    {pieData.map((entry, index) => (
                      <Cell
                        key={entry.label}
                        fill={SEGMENT_COLORS[index % SEGMENT_COLORS.length]}
                        opacity={hoveredIndex === null || hoveredIndex === index ? 1 : 0.42}
                        className="customer-segmentation-kpi__cell"
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="customer-segmentation-kpi__donut-center" aria-hidden>
                <span className="customer-segmentation-kpi__donut-total">
                  {localizeChartDigits(model.mixTotal.toLocaleString(), isRTL)}
                </span>
                <span className="customer-segmentation-kpi__donut-total-label">
                  {t("salesProduct.customers.segmentation.totalCustomers")}
                </span>
              </div>
            </div>
            {hoveredRow ? (
              <div
                className={`customer-segmentation-kpi__chart-tooltip customer-segmentation-kpi__chart-tooltip--floating customer-segmentation-kpi__chart-tooltip--${hoveredRow.segmentIndex === 0 ? "new" : "returning"}`}
              >
                <SegmentationTooltip row={hoveredRow} model={model} isRTL={isRTL} />
              </div>
            ) : null}
          </div>
        </div>

        <div className="customer-segmentation-kpi__stats">
          <ul className="customer-segmentation-kpi__stat-list">
            <li
              className={`customer-segmentation-kpi__stat-row customer-segmentation-kpi__stat-row--new ${
                model.emphasis === "new" ? "customer-segmentation-kpi__stat-row--emphasis" : ""
              }`.trim()}
            >
              <span
                className="customer-segmentation-kpi__stat-rail"
                style={{ backgroundColor: SEGMENT_COLORS[0] }}
                aria-hidden
              />
              <div className="customer-segmentation-kpi__stat-body">
                <div className="customer-segmentation-kpi__stat-top">
                  <div className="customer-segmentation-kpi__stat-titles">
                    <span className="customer-segmentation-kpi__stat-label">
                      {newLabel}
                      <DefinitionTooltip
                        label={newLabel}
                        info={segmentTooltips.new}
                        preferSide={isRTL ? "left" : "right"}
                        formulaTitle={t("salesProduct.shared.formula")}
                      />
                    </span>
                    <span className="customer-segmentation-kpi__stat-sublabel">
                      {t("salesProduct.customers.segmentation.newSublabel")}
                    </span>
                  </div>
                  <span className="customer-segmentation-kpi__stat-share">
                    {localizeChartDigits(`${model.newSharePctDisplay}%`, isRTL)}
                  </span>
                </div>
                <div className="customer-segmentation-kpi__stat-bottom">
                  <span className="customer-segmentation-kpi__stat-count">
                    {localizeChartDigits(model.newCount.toLocaleString(), isRTL)}
                  </span>
                </div>
              </div>
            </li>

            <li
              className={`customer-segmentation-kpi__stat-row customer-segmentation-kpi__stat-row--returning ${
                model.emphasis === "returning"
                  ? "customer-segmentation-kpi__stat-row--emphasis"
                  : ""
              }`.trim()}
            >
              <span
                className="customer-segmentation-kpi__stat-rail"
                style={{ backgroundColor: SEGMENT_COLORS[1] }}
                aria-hidden
              />
              <div className="customer-segmentation-kpi__stat-body">
                <div className="customer-segmentation-kpi__stat-top">
                  <div className="customer-segmentation-kpi__stat-titles">
                    <span className="customer-segmentation-kpi__stat-label">
                      {returningLabel}
                      <DefinitionTooltip
                        label={returningLabel}
                        info={segmentTooltips.returning}
                        align="end"
                        preferSide={isRTL ? "left" : "right"}
                        formulaTitle={t("salesProduct.shared.formula")}
                      />
                    </span>
                    <span className="customer-segmentation-kpi__stat-sublabel">
                      {t("salesProduct.customers.segmentation.returningSublabel")}
                    </span>
                  </div>
                  <span className="customer-segmentation-kpi__stat-share">
                    {localizeChartDigits(`${model.returningSharePctDisplay}%`, isRTL)}
                  </span>
                </div>
                <div className="customer-segmentation-kpi__stat-bottom">
                  <span className="customer-segmentation-kpi__stat-count">
                    {localizeChartDigits(model.returningCount.toLocaleString(), isRTL)}
                  </span>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};
